'use client';

import { IconBell, IconPlus, IconSend, IconQrcode, IconUser, IconCopy, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import BottomNav from './components/BottomNav';
import QRScanner from './components/QRScanner';
import PaymentModal from './components/PaymentModal';
import FundModal from './components/FundModal';
import type { UsdcWidgetConfig } from './types/widget';
import SendModal from './components/SendModal';

// Simulate payment database
const DEMO_PAYMENTS: Record<string, UsdcWidgetConfig> = {
  '856b4690-8b33-4877-9e21-43957c41ed81': {
    address: '0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045',
    vendorIcon: 'https://sqlc.dev/logo.png',
    productImage: 'https://sqlc.dev/logo.png',
    amount: 15.00,
    currency: '$',
    title: 'Coffee Payment',
    productName: 'Premium Coffee',
    timerDuration: 300,
    mode: 'modal',
    colors: {
      primary: '#4CD964',
      secondary: '#2C2C2E',
      text: '#FFFFFF',
      background: '#1C1C1E',
      accent: '#4CD964'
    }
  },
  '856b4690-8b33-4877-9e21-43957c41ed83': {
    address: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
    vendorIcon: 'https://sqlc.dev/logo.png',
    productImage: 'https://sqlc.dev/logo.png',
    amount: 45.99,
    currency: 'USDC',
    title: 'Premium Subscription',
    productName: 'Monthly Plan',
    timerDuration: 900,
    mode: 'modal',
    colors: {
      primary: '#0066FF',
      secondary: '#1A1A2E',
      text: '#FFFFFF',
      background: '#0A0A1E',
      accent: '#00D1FF'
    }
  }
};

// Simulate API fetch
const fetchPaymentData = async (txId: string): Promise<UsdcWidgetConfig | null> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Return data if it exists in our mock database
  return DEMO_PAYMENTS[txId] || null;
};

export default function Home() {
  const [showScanner, setShowScanner] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showScanToast, setShowScanToast] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<UsdcWidgetConfig>({
    address: '',
    amount: 0,
    currency: '$',
    title: 'Crypto Payment',
    productName: 'Product',
    timerDuration: 300,
    mode: 'modal',
    colors: {
      primary: '#4CD964',
      secondary: '#2C2C2E',
      text: '#FFFFFF',
      background: '#1C1C1E',
      accent: '#4CD964'
    },
    qrCode: undefined
  });
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isFundModalOpen, setIsFundModalOpen] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText('0x013ca...7f98bb');
    setCopied(true);
    setShowToast(true);
    setTimeout(() => {
      setCopied(false);
      setShowToast(false);
    }, 2000);
  };

  const parseQRData = async (data: string): Promise<UsdcWidgetConfig | null> => {
    try {
      // Case 1: transaction ID with ethereum: prefix
      if (data.startsWith('ethereum:')) {
        const txId = data.substring(9); // Remove 'ethereum:' prefix
        if (!txId || txId.length === 0) {
          setErrorMessage('Invalid QR code: Missing transaction ID');
          return null;
        }
        
        setIsLoading(true);
        try {
          const paymentData = await fetchPaymentData(txId);
          if (!paymentData) {
            setErrorMessage(`No payment data found for transaction: ${txId}`);
            return null;
          }
          return paymentData;
        } catch (error) {
          setErrorMessage('Error fetching payment data');
          console.error('Error fetching payment data:', error);
          return null;
        } finally {
          setIsLoading(false);
        }
      }
      
      // Case 2: Direct JSON data
      try {
        const parsed = JSON.parse(data);
        return {
          address: parsed.address || '',
          amount: Number(parsed.amount) || 0,
          currency: parsed.currency || '$',
          title: parsed.title || 'Crypto Payment',
          productName: parsed.productName || parsed.title || 'Product',
          timerDuration: parsed.timerDuration || 300,
          mode: parsed.mode || 'modal',
          colors: parsed.colors || {
            primary: '#4CD964',
            secondary: '#2C2C2E',
            text: '#FFFFFF',
            background: '#1C1C1E',
            accent: '#4CD964'
          },
          qrCode: parsed.qrCode || undefined
        };
      } catch (e) {
        // Case 3: Just an address
        if (data.startsWith('0x') && data.length >= 40) {
          return {
            address: data,
            amount: 0,
            currency: '$',
            title: 'Crypto Payment',
            productName: 'Send to Address',
            timerDuration: 300,
            mode: 'modal',
            colors: {
              primary: '#4CD964',
              secondary: '#2C2C2E',
              text: '#FFFFFF',
              background: '#1C1C1E',
              accent: '#4CD964'
            },
            qrCode: undefined
          };
        }
        
        // Case 4: Invalid data
        setErrorMessage('Invalid QR code data format');
        return null;
      }
    } catch (e) {
      setErrorMessage('Failed to process QR code data');
      console.error('Error processing QR data:', e);
      return null;
    }
  };

  const handleScan = async (data: string) => {
    console.log('QR Code scanned:', data);
    setScannedData(data);
    setErrorMessage(null);
    
    // Show loading state
    setIsLoading(true);
    
    // Parse and process the QR data
    const config = await parseQRData(data);
    setIsLoading(false);
    
    if (config) {
      setPaymentConfig(config);
      setShowScanner(false);
      setShowPaymentModal(true);
    } else {
      // If no config was returned, an error message should already be set
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  };

  const handleConfirmPayment = () => {
    setShowPaymentModal(false);
    setShowScanToast(true);
    setTimeout(() => {
      setShowScanToast(false);
    }, 2000);
  };

  return (
    <>
      <div className="flex flex-col min-h-[100dvh] bg-background text-white">
        {/* Header */}
        <header className="flex justify-between items-center p-4 safe-top">
          <button type="button" className="p-2 rounded-full bg-surface">
            <IconUser size={22} />
          </button>
          <div className="flex items-center px-4 py-2 rounded-full bg-surface">
            <span className="font-bold text-m">OW2</span>
          </div>
          <button type="button" className="p-2 rounded-full bg-surface">
            <IconBell size={22} />
          </button>
        </header>

        {/* Balance Section */}
        <section className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
          <h1 className="text-4xl sm:text-5xl font-bold">${(Math.random() * 1000).toFixed(2)}</h1>
          <button 
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface text-sm text-text-secondary hover:bg-surface/80 transition-colors"
          >
            <span className="font-mono">0x013ca...7f98bb</span>
            {copied ? (
              <IconCheck size={16} className="text-green-500" />
            ) : (
              <IconCopy size={16} />
            )}
          </button>
          
          {/* Action Buttons */}
          <div className="flex gap-6 sm:gap-8 mt-6">
            <button 
              type="button" 
              className="flex flex-col items-center gap-2 group"
              onClick={() => setIsFundModalOpen(true)}
            >
              <div className="p-3 sm:p-4 rounded-full bg-surface group-hover:bg-surface/80 group-hover:scale-105 transition-all">
                <IconPlus size={22} className="group-hover:text-primary transition-colors" />
              </div>
              <span className="text-sm group-hover:text-primary transition-colors">Fund</span>
            </button>
            <button 
              type="button" 
              className="flex flex-col items-center gap-2 group"
              onClick={() => setIsSendModalOpen(true)}
            >
              <div className="p-3 sm:p-4 rounded-full bg-surface group-hover:bg-surface/80 group-hover:scale-105 transition-all">
                <IconSend size={22} className="group-hover:text-primary transition-colors" />
              </div>
              <span className="text-sm group-hover:text-primary transition-colors">Send</span>
            </button>
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="flex flex-col items-center gap-2 group"
            >
              <div className="p-3 sm:p-4 rounded-full bg-surface group-hover:bg-surface/80 group-hover:scale-105 transition-all">
                <IconQrcode size={22} className="group-hover:text-primary transition-colors" />
              </div>
              <span className="text-sm group-hover:text-primary transition-colors">Pay</span>
            </button>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="mt-auto">
        </section>

        {/* Bottom Navigation */}
        <BottomNav />
      </div>

      {/* QR Scanner */}
      {showScanner && (
        <QRScanner
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
        />
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handleConfirmPayment}
        config={paymentConfig}
      />

      {/* Send Modal */}
      <SendModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        availableBalance={0.00942984}
        onSend={(data) => {
          console.log('Sending transaction:', data);
          setIsSendModalOpen(false);
        }}
      />

      {/* Fund Modal */}
      <FundModal
        isOpen={isFundModalOpen}
        onClose={() => setIsFundModalOpen(false)}
        availableBalance={0.00942984}
      />

      {/* Toast Notification for Scan */}
      <div className={`fixed bottom-20 left-1/2 transform -translate-x-1/2 transition-all duration-200 ${
        showScanToast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        <div className="px-4 py-2 rounded-lg bg-surface/80 backdrop-blur-sm text-sm text-white shadow-lg">
          Payment confirmed successfully
        </div>
      </div>

      {/* Toast Notification for Errors or Status */}
      <div className={`fixed bottom-20 left-1/2 transform -translate-x-1/2 transition-all duration-200 ${
        (showToast || isLoading) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        <div className={`px-4 py-2 rounded-lg backdrop-blur-sm text-sm text-white shadow-lg ${
          errorMessage ? 'bg-red-500/80' : 'bg-surface/80'
        }`}>
          {isLoading && !errorMessage ? 'Loading payment data...' : 
           errorMessage ? errorMessage :
           'Address copied to clipboard'}
        </div>
      </div>
    </>
  );
}
